import { ZENBU_OPTION } from '@constants';
import { masterDataApi } from '@services/masterDataApi';
import { storeApi } from '@services/storeApi';
import { handleShowToastError } from '@utils/helpers';
import { COMMON_CONSTANT, MODAL_TYPE, USER_ROLE } from '@utils/constants';
import { Form } from 'antd';
import { Table } from 'antd/lib';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EHttpStatusCode, TAxiosError } from 'types/service';
import {
  TGetAreaParam,
  TGetCompanyParam,
  TGetStoreMasterDetailRes,
  TGetStoresMasterReq,
  TGetStoresMasterTable,
  TGetStoreParam,
  TRegisStoreMasterReq,
  TUpdateStoreMasterReq,
} from 'types/types';
import { getColumns } from '../columns';
import {
  REGEX_EMAIL,
  REGEX_STORE_CODE,
  STORE_PAGE_CONSTANT,
} from '../constant';
import { useCurrentUserRole } from '@hooks';

const { FORM_NAME } = STORE_PAGE_CONSTANT;

// Translation keys reused across multiple modal states (avoids duplicated literals)
const TRANSLATION_KEY = {
  FIELD_INVALID_FORMAT: 'STORE_PAGE.FIELD_INVALID_FORMAT',
  DELETE_SUCCESS_TITLE: 'STORE_PAGE.DELETE_SUCCESS_TITLE',
  DELETE_FAIL_MESSAGE: 'STORE_PAGE.DELETE_FAIL_MESSAGE',
  NOT_FOUND_STORE_CODE: 'STORE_PAGE.NOT_FOUND_STORE_CODE',
};

type TModalText = { title: string; okText: string; message: string };

const EMPTY_MODAL_TEXT: TModalText = { title: '', okText: '', message: '' };

// ***** renderTextModal helpers (split to keep cyclomatic complexity low) *****
const getFormModalText = (
  modalType: string,
  t: (key: string) => string
): TModalText | null => {
  switch (modalType) {
    case MODAL_TYPE.REGISTRATION:
      return {
        title: t('STORE_PAGE.REGIS_STORE'),
        okText: t('COMMON.NEXT'),
        message: '',
      };
    case MODAL_TYPE.DETAIL:
      return {
        title: t('STORE_PAGE.DETAIL_STORE'),
        okText: t('COMMON.CLOSE'),
        message: '',
      };
    case MODAL_TYPE.UPDATE:
      return {
        title: t('STORE_PAGE.UPDATE_STORE'),
        okText: t('COMMON.NEXT'),
        message: '',
      };
    case MODAL_TYPE.CONFIRM_REGISTRATION:
      return {
        title: t('STORE_PAGE.REGIS_STORE'),
        okText: t('COMMON.UPDATE'),
        message: '',
      };
    case MODAL_TYPE.CONFIRM_UPDATE:
      return {
        title: t('STORE_PAGE.UPDATE_STORE'),
        okText: t('COMMON.CHANGE'),
        message: '',
      };
    default:
      return null;
  }
};

const getSuccessModalText = (
  modalType: string,
  t: (key: string) => string
): TModalText | null => {
  switch (modalType) {
    case MODAL_TYPE.SUCCESS_REGISTRATION:
      return {
        title: t('STORE_PAGE.REGIS_SUCCESS_TITLE'),
        okText: t('COMMON.OK'),
        message: t('STORE_PAGE.REGIS_SUCCESS_MESSAGE'),
      };
    case MODAL_TYPE.SUCCESS_UPDATE:
      return {
        title: t('STORE_PAGE.UPDATE_SUCCESS_TITLE'),
        okText: t('COMMON.OK'),
        message: t('STORE_PAGE.UPDATE_SUCCESS_MESSAGE'),
      };
    case MODAL_TYPE.SUCCESS_DELETE:
      return {
        title: t(TRANSLATION_KEY.DELETE_SUCCESS_TITLE),
        okText: t('COMMON.OK'),
        message: t('STORE_PAGE.DELETE_SUCCESS_MESSAGE'),
      };
    default:
      return null;
  }
};

const getFailOrErrorModalText = (
  modalType: string,
  t: (key: string) => string
): TModalText | null => {
  switch (modalType) {
    case MODAL_TYPE.FAIL_REGISTRATION:
      return {
        title: t(TRANSLATION_KEY.DELETE_SUCCESS_TITLE),
        okText: t('COMMON.OK'),
        message: t('STORE_PAGE.REGIS_FAIL_MESSAGE'),
      };
    case MODAL_TYPE.FAIL_UPDATE:
      return {
        title: t(TRANSLATION_KEY.DELETE_SUCCESS_TITLE),
        okText: t('COMMON.OK'),
        message: t('STORE_PAGE.UPDATE_FAIL_MESSAGE'),
      };
    case MODAL_TYPE.FAIL_DELETE:
      return {
        title: t(TRANSLATION_KEY.DELETE_SUCCESS_TITLE),
        okText: t('COMMON.OK'),
        message: t(TRANSLATION_KEY.DELETE_FAIL_MESSAGE),
      };
    case t(TRANSLATION_KEY.NOT_FOUND_STORE_CODE):
      return {
        title: t(TRANSLATION_KEY.DELETE_FAIL_MESSAGE),
        okText: t('COMMON.OK'),
        message: t('STORE_PAGE.NOT_FOUND_STORE'),
      };
    case COMMON_CONSTANT.IMPORT_MASTER_DATA_ERROR_CODE
      .COMMON_CHECK_FOUND_BEFORE_UPDATE:
      return {
        title: t(TRANSLATION_KEY.DELETE_FAIL_MESSAGE),
        okText: t('COMMON.OK'),
        message: t('COMMON_MSG.DATA_NOT_EXIST'),
      };
    default:
      return null;
  }
};

// ***** handleKeyPress helper (split to keep cyclomatic complexity low) *****
const isKeyPressBlocked = (
  keyCode: string,
  parts: string[],
  currentPart: string,
  isTextSelected: boolean
): boolean => {
  if (!/[0-9.]/.test(keyCode)) {
    return true;
  }

  if (parts.length > 4 && !isTextSelected) {
    return true;
  }

  if (currentPart.length >= 3 && keyCode !== '.' && !isTextSelected) {
    return true;
  }

  return (
    keyCode === '.' &&
    (currentPart.length === 0 || parts.length >= 4) &&
    !isTextSelected
  );
};

function useStorePage() {
  const currentUserRole = useCurrentUserRole();
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [formModal] = Form.useForm();
  const companyWatch = Form.useWatch(FORM_NAME.COMPANY_MODAL, formModal);
  const areaWatch = Form.useWatch(FORM_NAME.AREA_MODAL, formModal);
  const storeCodeWatch = Form.useWatch(FORM_NAME.STORE_CODE_MODAL, formModal);
  const storeNameWatch = Form.useWatch(FORM_NAME.STORE_NAME_MODAL, formModal);

  const [isLoading, setIsLoading] = useState(true);
  const [companyDropdownData, setCompanyDropdownData] = useState<any>([]);
  const [areaDropdownData, setAreaDropdownData] = useState<any>([]);
  const [storeDropdownData, setStoreDropdownData] = useState<any>([]);
  const [listStores, setListStores] = useState<TGetStoresMasterTable[]>([]);
  const [store, setStore] = useState<TGetStoresMasterTable | null>(null);
  const [totalRecords, setTotalRecords] = useState(0); // records table
  const [pageIndex, setPageIndex] = useState(0);
  const [rowKey, setRowKey] = useState<number | null>(null);

  const [loadingModal, setLoadingModal] = useState(false);
  const [companiesModal, setCompaniesModal] = useState<any[]>([]);
  const [areasModal, setAreasModal] = useState<any>([]);
  const [recordId, setRecordId] = useState('');

  const [modalType, setModalType] = useState('');
  const [modalTypeConfirm, setModalTypeConfirm] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [modalConfirmFinal, setModalConfirmFinal] = useState(false);
  const [modalSuccess, setModalSuccess] = useState(false);
  const [modalFail, setModalFail] = useState(false);
  const [modalDelete, setModalDelete] = useState(false);

  const disableBtnOther = false;
  const disableFields =
    modalType === MODAL_TYPE.CONFIRM_REGISTRATION ||
    modalType === MODAL_TYPE.CONFIRM_UPDATE ||
    modalType === MODAL_TYPE.DETAIL;
  const disableFieldsEdit = modalType === MODAL_TYPE.UPDATE || disableFields;
  const isNotDetailModal = modalType !== MODAL_TYPE.DETAIL;
  const isStoreNameBlank =
    storeNameWatch && !storeNameWatch.replace(/\s/g, '').length;
  const missingRequiredFields =
    !companyWatch || !areaWatch || !storeCodeWatch || !storeNameWatch;
  const disableBtnNext =
    isNotDetailModal && (missingRequiredFields || isStoreNameBlank);

  const tblRef: Parameters<typeof Table>[0]['ref'] = useRef(null);

  // ***** FORM SEARCH *****
  const handleSearch = (values: any) => {
    tblRef.current?.scrollTo({ top: 0 });
    setPageIndex(1);
    handleGetStoresSearch({
      ...values,
      page: 0,
    });
  };

  const handleChangeCompany = (val: string) => {
    form.setFieldValue(FORM_NAME.AREA, ZENBU_OPTION.value);
    form.setFieldValue(FORM_NAME.STORE, ZENBU_OPTION.value);
    setAreaDropdownData([ZENBU_OPTION]);
    setStoreDropdownData([ZENBU_OPTION]);
    if (val) {
      handleGetAreas({ companyId: val, storeId: '' }, false);
    } else {
      handleGetAreas({ companyId: '', storeId: '' }, false);
    }
  };

  const handleChangeCompaniesModal = (val: string) => {
    formModal.setFieldValue(FORM_NAME.AREA_MODAL, undefined);
    setAreasModal([]);
    handleGetAreas({ companyId: val, storeId: '' }, true);
  };

  const handleChangeArea = (val: string) => {
    form.setFieldValue(FORM_NAME.STORE, ZENBU_OPTION.value);
    setStoreDropdownData([ZENBU_OPTION]);
    handleGetStoreByCompany({
      companyId: form.getFieldValue(FORM_NAME.COMPANY),
      sectorId: val,
    });
  };

  // ***** FORM MODAL *****
  const handleCloseModal = () => {
    setModalOpen(false);
    setModalType('');
    setRowKey(null);
  };

  const handleCloseModalSuccessFail = () => {
    setModalSuccess(false);
    setModalFail(false);
    setModalType('');
    setRowKey(null);
  };

  const handleCloseModalDelete = () => {
    setStore(null);
    setRowKey(null);
    setModalDelete(false);
  };

  const handleCloseModalConfirmFinal = () => {
    setModalConfirmFinal(false);
    setModalTypeConfirm('');
  };

  const handleOpenModalRegis = () => {
    setModalOpen(true);
    setAreasModal([]);
    setModalType(MODAL_TYPE.REGISTRATION);
  };

  const handleOpenModalDelete = (record: TGetStoresMasterTable) => {
    setModalDelete(true);
    setRowKey(record.key);
    setStore(record);
    setModalType(MODAL_TYPE.DELETE);
  };

  const handleOpenModalDetail = (record: TGetStoresMasterTable) => {
    setModalOpen(true);
    setModalType(MODAL_TYPE.DETAIL);
    setRowKey(record.key);
    handleGetDetailStore(record.id);
  };

  const handleOpenModalUpdate = (record: TGetStoresMasterTable) => {
    handleGetDetailStore(record.id);
    setRowKey(record.key);
    setModalOpen(true);
    setModalType(MODAL_TYPE.UPDATE);
  };

  const handleFormResetFieldError = () => {
    formModal.setFields([
      {
        name: [FORM_NAME.STORE_CODE_MODAL],
        errors: [],
      },
      {
        name: [FORM_NAME.EMAIL_MODAL],
        errors: [],
      },
      {
        name: [FORM_NAME.MOBILE_PHONE_MODAL],
        errors: [],
      },
    ]);
  };

  const getStoreCodeValidationError = (): boolean => {
    const validateStoreCode = storeCodeWatch
      ? REGEX_STORE_CODE.test(storeCodeWatch.trim())
      : '';
    return !validateStoreCode;
  };

  const getEmailValidationError = (): boolean => {
    const emailWatch = formModal.getFieldValue(FORM_NAME.EMAIL_MODAL);
    const validateEmail = emailWatch ? REGEX_EMAIL.test(emailWatch.trim()) : '';
    return !validateEmail && validateEmail !== '';
  };

  const getPhoneValidationError = (): boolean => {
    const mobilePhoneWatch = formModal.getFieldValue(
      FORM_NAME.MOBILE_PHONE_MODAL
    );
    return !!mobilePhoneWatch && mobilePhoneWatch.toString().length < 10;
  };

  const applyFormValidationErrors = (): boolean => {
    let checkError = false;

    if (getStoreCodeValidationError()) {
      formModal.setFields([
        {
          name: [FORM_NAME.STORE_CODE_MODAL],
          errors: [t(TRANSLATION_KEY.FIELD_INVALID_FORMAT)],
        },
      ]);
      checkError = true;
    }

    if (getEmailValidationError()) {
      formModal.setFields([
        {
          name: [FORM_NAME.EMAIL_MODAL],
          errors: [t(TRANSLATION_KEY.FIELD_INVALID_FORMAT)],
        },
      ]);
      checkError = true;
    }

    if (getPhoneValidationError()) {
      formModal.setFields([
        {
          name: [FORM_NAME.MOBILE_PHONE_MODAL],
          errors: [t(TRANSLATION_KEY.FIELD_INVALID_FORMAT)],
        },
      ]);
      checkError = true;
    }

    return checkError;
  };

  const submitValidatedForm = () => {
    const params: TRegisStoreMasterReq = {
      companyId: companyWatch,
      sectorId: areaWatch,
      storeCode: storeCodeWatch,
      storeName: storeNameWatch,
      email: formModal.getFieldValue(FORM_NAME.EMAIL_MODAL),
      phone: formModal.getFieldValue(FORM_NAME.MOBILE_PHONE_MODAL),
      isValidationOnly: true,
    };
    if (modalType === MODAL_TYPE.REGISTRATION) {
      handleValidateRegisStore(params);
    }

    if (modalType === MODAL_TYPE.UPDATE) {
      handleValidateUpdateStore({ ...params, id: recordId });
    }
  };

  const handleValidateForm = () => {
    handleFormResetFieldError();

    const checkError = applyFormValidationErrors();

    if (!checkError) {
      submitValidatedForm();
    }
  };

  const handleSetFormValues = (values: TGetStoreMasterDetailRes) => {
    setRecordId(values.id);
    formModal.setFieldValue(FORM_NAME.COMPANY_MODAL, values.companyId);
    formModal.setFieldValue(FORM_NAME.AREA_MODAL, values.sectorId);
    formModal.setFieldValue(FORM_NAME.STORE_CODE_MODAL, values.storeCode);
    formModal.setFieldValue(FORM_NAME.STORE_NAME_MODAL, values.storeName);
    formModal.setFieldValue(FORM_NAME.EMAIL_MODAL, values.email);
    formModal.setFieldValue(FORM_NAME.MOBILE_PHONE_MODAL, values.phone);
  };

  const handleOpenModalConfirmFinal = () => {
    setModalConfirmFinal(true);
    if (modalType === MODAL_TYPE.CONFIRM_REGISTRATION) {
      setModalTypeConfirm(MODAL_TYPE.CONFIRM_REGISTRATION_FINAL);
    }

    if (modalType === MODAL_TYPE.CONFIRM_UPDATE) {
      setModalTypeConfirm(MODAL_TYPE.CONFIRM_UPDATE_FINAL);
    }
  };

  const handleOkModalConfirmFinal = () => {
    const params: TRegisStoreMasterReq = {
      companyId: companyWatch,
      sectorId: areaWatch,
      storeCode: storeCodeWatch,
      storeName: storeNameWatch,
      email: formModal.getFieldValue(FORM_NAME.EMAIL_MODAL),
      phone: formModal.getFieldValue(FORM_NAME.MOBILE_PHONE_MODAL),
      isValidationOnly: false,
    };

    if (modalTypeConfirm === MODAL_TYPE.CONFIRM_REGISTRATION_FINAL) {
      handleCreateStore(params);
    }

    if (modalTypeConfirm === MODAL_TYPE.CONFIRM_UPDATE_FINAL) {
      handleUpdateStore({ ...params, id: recordId });
    }
  };

  const handleOkModalDelete = () => {
    if (store) {
      handleDeleteStore(store.id);
    }
  };

  const handleKeyPress = (e: any) => {
    const keyCode = e.key;
    const inputValue = e.target.value;
    const selectionStart = e.target.selectionStart;
    const selectionEnd = e.target.selectionEnd;

    const parts = inputValue.split('.');
    const partIndex =
      inputValue.substring(0, selectionStart).split('.').length - 1;
    const currentPart = parts[partIndex];

    const isTextSelected = selectionEnd > selectionStart;

    if (isKeyPressBlocked(keyCode, parts, currentPart, isTextSelected)) {
      e.preventDefault();
    }
  };

  //***** API INIT *****
  const handleInitStoresForCompany = async (companyId: string) => {
    const responseStore = await masterDataApi.getStore({
      companyId,
      sectorId: ZENBU_OPTION.value,
    });
    if (!responseStore.ok || !responseStore.data.length) {
      setStoreDropdownData([ZENBU_OPTION]);
      form.setFieldValue(FORM_NAME.STORE, ZENBU_OPTION.value);
      return;
    }

    const storeData = responseStore.data.map((val) => ({
      value: val.id,
      label: val.storeCode + '_' + val.storeName,
    }));
    setStoreDropdownData([ZENBU_OPTION, ...storeData]);
    form.setFieldValue(FORM_NAME.STORE, ZENBU_OPTION.value);
  };

  const handleInitAreaForCompany = async (companyId: string) => {
    const responseArea = await masterDataApi.getArea({
      companyId,
      storeId: '',
    });
    if (!responseArea.ok || !responseArea.data.length) {
      setAreaDropdownData([ZENBU_OPTION]);
      form.setFieldValue(FORM_NAME.AREA, ZENBU_OPTION.value);
      await handleInitStoresForCompany(companyId);
      return;
    }

    const areaData = responseArea.data.map((val) => ({
      value: val.id,
      label: val.sectorCode + '_' + val.sectorName,
    }));
    setAreaDropdownData([ZENBU_OPTION, ...areaData]);
    form.setFieldValue(FORM_NAME.AREA, ZENBU_OPTION.value);

    await handleInitStoresForCompany(companyId);
  };

  const handleInitialPage = async (payload: TGetCompanyParam) => {
    try {
      const responseCompany = await masterDataApi.getCompany(payload);
      if (!responseCompany.ok || !responseCompany.data.length) {
        return;
      }

      const companyData = responseCompany.data.map((val) => ({
        value: val.id,
        label: val.companyCode + '_' + val.companyName,
      }));
      setCompanyDropdownData([ZENBU_OPTION, ...companyData]);
      setCompaniesModal(companyData);
      form.setFieldValue(FORM_NAME.COMPANY, companyData[0].value);

      await handleInitAreaForCompany(responseCompany.data[0].id);

      await handleGetStoresSearch({
        companyId: responseCompany.data.length
          ? responseCompany.data[0].id
          : '',
        sectorId: ZENBU_OPTION.value,
        storeId: ZENBU_OPTION.value,
        page: 0,
      });
    } catch (error) {
      handleShowToastError(error, '');
    } finally {
      setIsLoading(false);
    }
  };

  //***** API SEARCH *****
  const handleGetAreas = async (payload: TGetAreaParam, isModal: boolean) => {
    try {
      const response = await masterDataApi.getArea(payload);
      if (response.ok && response.data.length) {
        const areaData = response.data.map((val) => ({
          value: val.id,
          label: val.sectorCode + '_' + val.sectorName,
        }));
        if (isModal) {
          setAreasModal(areaData);
        } else {
          setAreaDropdownData([ZENBU_OPTION, ...areaData]);
          form.setFieldValue(
            FORM_NAME.AREA,
            payload.companyId ? areaData[0].value : ''
          );
          await handleGetStoreByCompany({
            companyId: payload.companyId,
            sectorId: payload.companyId ? areaData[0].value : '',
          });
        }
      }
    } catch (error) {
      handleShowToastError(error, '');
    }
  };

  const handleGetStoreByCompany = async (payload: TGetStoreParam) => {
    try {
      const response = await masterDataApi.getStore(payload);
      if (response.ok && response.data.length) {
        const storeData = response?.data?.map((val) => ({
          value: val.id,
          label: val.storeCode + '_' + val.storeName,
        }));
        setStoreDropdownData([ZENBU_OPTION, ...storeData]);
        form.setFieldValue(
          FORM_NAME.STORE,
          payload.sectorId ? storeData[0].value : ''
        );
      }
    } catch (error) {
      handleShowToastError(error, '');
    }
  };

  const handleScroll = (e: any) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;

    if (
      scrollTop + clientHeight >= scrollHeight - 5 &&
      listStores.length < totalRecords
    ) {
      setPageIndex((prev) => prev + 1);
      handleGetStoresScroll({
        companyId: form.getFieldValue(FORM_NAME.COMPANY),
        sectorId: form.getFieldValue(FORM_NAME.AREA),
        storeId: form.getFieldValue(FORM_NAME.STORE),
        page: pageIndex + 1,
      });
    }
  };

  const handleGetStoresSearch = async (payload: TGetStoresMasterReq) => {
    setIsLoading(true);
    try {
      const response = await storeApi.searchStores(payload);
      if (response.ok) {
        setListStores(
          response.data.map((item, index) => ({
            ...item,
            key: index + 1,
          }))
        );
        setPageIndex(1);
        setTotalRecords(response.pagination?.totalCount as number);
      }
    } catch (error) {
      handleShowToastError(error, '');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGetStoresScroll = async (payload: TGetStoresMasterReq) => {
    setIsLoading(true);
    try {
      const response = await storeApi.searchStores(payload);
      const newListStores = [...listStores, ...response.data].map(
        (item, index) => ({
          ...item,
          key: index + 1,
        })
      );
      setListStores(newListStores);
    } catch (error) {
      handleShowToastError(error, '');
    } finally {
      setIsLoading(false);
    }
  };

  //***** API MODAL *****
  const handleValidateRegisStore = async (payload: TRegisStoreMasterReq) => {
    try {
      const response = await storeApi.createStore(payload);
      if (response.ok) {
        setModalType(MODAL_TYPE.CONFIRM_REGISTRATION);
      }
    } catch (error) {
      const err = error as TAxiosError;
      if (
        err.status === EHttpStatusCode.BAD_REQUEST &&
        err.data?.errors?.[0].code ===
          COMMON_CONSTANT.IMPORT_MASTER_DATA_ERROR_CODE.STORE_ALREADY_EXISTED
      ) {
        formModal.setFields([
          {
            name: FORM_NAME.STORE_CODE_MODAL,
            errors: [t('COMMON_MSG.DATA_EXIST')],
          },
        ]);
        return;
      }
      handleShowToastError(error, '');
    }
  };

  const handleValidateUpdateStore = async (payload: TUpdateStoreMasterReq) => {
    try {
      const response = await storeApi.updateStore(payload);
      if (response.ok) {
        setModalType(MODAL_TYPE.CONFIRM_UPDATE);
      }
    } catch (error) {
      const err = error as TAxiosError;
      if (
        err?.status === EHttpStatusCode.BAD_REQUEST &&
        err?.data?.errors?.[0]?.code ===
          COMMON_CONSTANT.IMPORT_MASTER_DATA_ERROR_CODE.NOT_FOUND_STORE_CODE
      ) {
        setModalFail(true);
        setModalOpen(false);
        setModalType(t(TRANSLATION_KEY.NOT_FOUND_STORE_CODE));
        return;
      }
      handleShowToastError(error, '');
    }
  };

  const handleCreateStore = async (payload: TRegisStoreMasterReq) => {
    try {
      const response = await storeApi.createStore(payload);
      if (response.ok) {
        setModalType(MODAL_TYPE.SUCCESS_REGISTRATION);
        setModalSuccess(true);
        setModalConfirmFinal(false);
        setModalOpen(false);

        tblRef.current?.scrollTo({ top: 0 });
        setPageIndex(1);
        handleGetStoresSearch({
          sectorId: form.getFieldValue(FORM_NAME.AREA),
          companyId: form.getFieldValue(FORM_NAME.COMPANY),
          storeId: form.getFieldValue(FORM_NAME.STORE),
          page: 0,
        });
      }
    } catch (error) {
      setModalType(MODAL_TYPE.FAIL_REGISTRATION);
      setModalFail(true);
      setModalConfirmFinal(false);
      setModalOpen(false);
      handleShowToastError(error, '');
    }
  };

  const handleUpdateStore = async (payload: TUpdateStoreMasterReq) => {
    try {
      const response = await storeApi.updateStore(payload);
      if (response.ok) {
        setModalType(MODAL_TYPE.SUCCESS_UPDATE);
        setModalSuccess(true);
        setModalConfirmFinal(false);
        setModalOpen(false);

        tblRef.current?.scrollTo({ top: 0 });
        setPageIndex(1);
        handleGetStoresSearch({
          sectorId: form.getFieldValue(FORM_NAME.AREA),
          companyId: form.getFieldValue(FORM_NAME.COMPANY),
          storeId: form.getFieldValue(FORM_NAME.STORE),
          page: 0,
        });
      }
    } catch (error) {
      const err = error as TAxiosError;
      setModalConfirmFinal(false);
      setModalOpen(false);
      if (
        err?.status === EHttpStatusCode.BAD_REQUEST &&
        err?.data?.errors?.[0]?.code ===
          COMMON_CONSTANT.IMPORT_MASTER_DATA_ERROR_CODE.NOT_FOUND_STORE_CODE
      ) {
        setModalFail(true);
        setModalType(t(TRANSLATION_KEY.NOT_FOUND_STORE_CODE));
        return;
      }
      setModalType(MODAL_TYPE.FAIL_UPDATE);
      setModalFail(true);
      handleShowToastError(error, '');
    }
  };

  const handleGetDetailStore = async (payload: string) => {
    setLoadingModal(true);

    try {
      const response = await storeApi.getDetailStore(payload);
      if (response.ok) {
        handleSetFormValues(response.data);
        await handleGetAreas(
          { companyId: response.data.companyId, storeId: '' },
          true
        );
      }
    } catch (error) {
      handleShowToastError(error, '');
    } finally {
      setLoadingModal(false);
    }
  };

  const handleDeleteStore = async (payload: string) => {
    setIsLoading(true);
    try {
      const response = await storeApi.deleteStore(payload);
      if (response.ok) {
        setListStores(
          [
            ...listStores.filter(function (item) {
              return item.id !== payload;
            }),
          ].map((item, index) => ({ ...item, key: index + 1 }))
        );
        setModalType(MODAL_TYPE.SUCCESS_DELETE);
        setModalSuccess(true);
        handleCloseModalDelete();

        const _response = await storeApi.searchStores({
          sectorId: form.getFieldValue(FORM_NAME.AREA),
          companyId: form.getFieldValue(FORM_NAME.COMPANY),
          storeId: form.getFieldValue(FORM_NAME.STORE),
          page: 0,
          pageSize: 1,
        });
        if (_response.ok) {
          setTotalRecords(_response.pagination?.totalCount as number);
        }
      }
    } catch (error) {
      setModalDelete(false);
      setModalFail(true);
      setRowKey(null);

      const err = error as TAxiosError;
      if (
        err?.status === EHttpStatusCode.BAD_REQUEST &&
        err?.data?.errors?.[0]?.code ===
          COMMON_CONSTANT.IMPORT_MASTER_DATA_ERROR_CODE.NOT_FOUND_STORE_CODE
      ) {
        setModalType(t(TRANSLATION_KEY.NOT_FOUND_STORE_CODE));
        return;
      }
      setModalType(MODAL_TYPE.FAIL_DELETE);
      handleShowToastError(error, '');
    } finally {
      setIsLoading(false);
    }
  };

  // ***** MEMO *****
  const columns = useMemo(() => {
    const columns = getColumns({
      translate: t,
      disableBtnOther,
      handleOpenModalDelete,
      handleOpenModalDetail,
      handleOpenModalUpdate,
    });
    return columns;
  }, [t]);

  const renderTextModal = useMemo(() => {
    return (
      getFormModalText(modalType, t) ??
      getSuccessModalText(modalType, t) ??
      getFailOrErrorModalText(modalType, t) ??
      EMPTY_MODAL_TEXT
    );
  }, [modalType]);

  const renderTextModalConfirm = useMemo(() => {
    switch (modalTypeConfirm) {
      case MODAL_TYPE.CONFIRM_REGISTRATION_FINAL:
        return {
          title: t('STORE_PAGE.REGIS_CONFIRM_FINAL'),
          okText: t('COMMON.OK'),
          message: t('STORE_PAGE.REGIS_CONFIRM_FINAL_MESSAGE'),
        };
      case MODAL_TYPE.CONFIRM_UPDATE_FINAL:
        return {
          title: t('STORE_PAGE.UPDATE_CONFIRM_FINAL'),
          okText: t('COMMON.OK'),
          message: t('STORE_PAGE.UPDATE_CONFIRM_FINAL_MESSAGE'),
        };

      default:
        return {
          title: '',
          okText: '',
          message: '',
        };
    }
  }, [modalTypeConfirm]);

  const handleClearSearch = () => {
    form?.resetFields();
    setAreaDropdownData([]);
    setStoreDropdownData(false);
  };

  // ****** EFFECT ******
  useEffect(() => {
    if (currentUserRole !== USER_ROLE.STORE_OWNER) {
      handleInitialPage({ storeId: '' });
    }
  }, []);

  return {
    state: {
      isLoading,
      companyDropdownData,
      areaDropdownData,
      storeDropdownData,
      listStores,
      store,
      rowKey,
      loadingModal,
      companiesModal,
      areasModal,
      modalType,
      modalTypeConfirm,
      modalOpen,
      modalConfirmFinal,
      modalSuccess,
      modalFail,
      modalDelete,
      disableFields,
      disableFieldsEdit,
      disableBtnNext,
      tblRef,
      form,
      formModal,
      columns,
      renderTextModal,
      renderTextModalConfirm,
    },
    handler: {
      setModalType,
      handleSearch,
      handleChangeCompany,
      handleChangeCompaniesModal,
      handleChangeArea,
      handleCloseModal,
      handleCloseModalSuccessFail,
      handleCloseModalDelete,
      handleCloseModalConfirmFinal,
      handleOpenModalRegis,
      handleOpenModalDelete,
      handleOpenModalDetail,
      handleOpenModalUpdate,
      handleValidateForm,
      handleOpenModalConfirmFinal,
      handleOkModalConfirmFinal,
      handleOkModalDelete,
      handleKeyPress,
      handleScroll,
      handleClearSearch,
    },
  };
}

export default useStorePage;
